import {generateTokensFromObject} from '../__utils';
import {space} from '../space';

import {
  baseTypography,
  fontFamilyTokens,
  fontWeightTokens,
  remify,
} from './_shared';
import type {Sizing} from './_shared';

/**
 * Values derived from Spring Foundations figma
 *
 * @See https://www.figma.com/design/kcg95u086DD7Sa9or8Bwpq/%F0%9F%8C%B1--Foundations---Spring-2.0?node-id=10933-3576&t=onU5m7tJSA9Y9NHT-4
 */
const typography: Sizing['typography'] = {
  ...baseTypography,
  fontFamily: {
    ...baseTypography.fontFamily,
    // System styles
    title: fontFamilyTokens.sansSerif,
    heading: fontFamilyTokens.sansSerif,
    body: fontFamilyTokens.sansSerif,
    helpText: fontFamilyTokens.sansSerif,
    code: fontFamilyTokens.mono,
    // Branded styles
    brandedDisplay1: fontFamilyTokens.branded,
    brandedDisplay2: fontFamilyTokens.branded,
    brandedDisplay3: fontFamilyTokens.branded,
    brandedDisplay4: fontFamilyTokens.branded,
    brandedDisplay5: fontFamilyTokens.branded,
    brandedDisplay6: fontFamilyTokens.branded,
    brandedDisplaySubtitle: fontFamilyTokens.branded,
    brandedHeading: fontFamilyTokens.branded,
    brandedBody: fontFamilyTokens.branded,
    brandedHelpText: fontFamilyTokens.branded,
  },
  fontWeight: {
    ...baseTypography.fontWeight,
    medium: 600,
    // System styles
    title: fontWeightTokens.semibold,
    heading: fontWeightTokens.semibold,
    body: fontWeightTokens.normal,
    helpText: fontWeightTokens.normal,
    code: fontWeightTokens.normal,
    // Branded styles
    brandedDisplay1: fontWeightTokens.semibold,
    brandedDisplay2: fontWeightTokens.semibold,
    brandedDisplay3: fontWeightTokens.semibold,
    brandedDisplay4: fontWeightTokens.semibold,
    brandedDisplay5: fontWeightTokens.semibold,
    brandedDisplay6: fontWeightTokens.semibold,
    brandedDisplaySubtitle: fontWeightTokens.normal,
    brandedHeading: fontWeightTokens.semibold,
    brandedBody: fontWeightTokens.normal,
    brandedHelpText: fontWeightTokens.normal,
  },
  fontSize: {
    // System styles
    title: remify(13),
    heading: remify(11.5),
    body: remify(11.5),
    helpText: remify(11.5),
    code: remify(11.5),
    // Branded styles
    brandedDisplay1: `clamp(${remify(32)}, 6vw, ${remify(84)})`,
    brandedDisplay2: `clamp(${remify(32)}, 6vw, ${remify(60)})`,
    brandedDisplay3: `clamp(${remify(24)}, 5vw, ${remify(32)})`,
    brandedDisplay4: `clamp(${remify(18)}, 4vw, ${remify(24)})`,
    brandedDisplay5: remify(18),
    brandedDisplay6: remify(16),
    brandedDisplaySubtitle: remify(18),
    brandedHeading: remify(16),
    brandedBody: remify(14),
    brandedHelpText: remify(12),
  },
  lineHeight: {
    // System styles
    title: '1rem',
    heading: '1rem',
    body: '1rem',
    helpText: '1rem',
    code: '1rem',
    // Branded styles
    brandedDisplay1: '96%',
    brandedDisplay2: '104%',
    brandedDisplay3: '104%',
    brandedDisplay4: '130%',
    brandedDisplay5: '130%',
    brandedDisplay6: '130%',
    brandedDisplaySubtitle: '160%',
    brandedHeading: '160%',
    brandedBody: remify(24),
    brandedHelpText: remify(16),
  },
} as const;

const ui: Sizing['ui'] = {
  accordion: {
    adornment: {
      rightPosition: '28px',
    },
    button: {
      padding: {
        comfortable: `${space[2]} ${space[1]} ${space[2]} ${space[2]}`,
        compact: `${space[1]} ${space[1]} ${space[1]} ${space[2]}`,
      },
    },
  },
  activeCursor: 'default',
  autocomplete: {
    clearButton: {
      right: space[6], // 24px (dropdown trigger width)
    },
    control: {
      padding: `3px 3px`, // Accounts for the 1px border, to make this a total of `4px 4px`
    },
    input: {
      height: space[4],
      padding: space[1],
    },
    label: {
      virtualized: {
        padding: `${space[2]} ${space[2]} ${space[1]}`,
      },
    },
    listBox: {
      padding: `${space[1]} 0px`,
    },
    option: {
      minHeight: space[4],
      padding: `${space[1]} ${space[2]}`,
    },
  },
  avatar: {
    xSmall: space[4],
    small: space[5],
    medium: space[6],
    large: space[10],
    xLarge: space[16],
  },
  avatarGroup: {
    spacing: {
      xSmall: `-${space[1]}`,
      small: `-${space[1]}`,
      medium: `-${space[1]}`,
      large: `-${space[2]}`,
      xLarge: `-${space[3]}`,
    },
    rowItem: {
      margin: space[2],
      padding: `${space[1]} ${space[1]} ${space[1]} ${space[2]}`,
    },
  },
  button: {
    padding: {
      comfortable: `${space[1]} ${space[2]}`,
      compact: space[1],
    },
  },
  checkbox: {
    boxSize: '12px !important', // !important seems necessary for `cms-csv-ref-new-fields.spec.ts` to pass
    checkSize: '10px !important', // !important seems necessary for `cms-csv-ref-new-fields.spec.ts` to pass
  },
  dataTable: {
    filterPopover: {
      filterPopoverSearchPadding: space[2],
      filterPopoverContentPadding: `${space[2]}`,
      filterPopoverSearchEmptyStatePadding: `${space[2]} ${space[0]}`,
    },
    bulkActionsBar: {
      containerPadding: `${space[2]} ${space[0]}`,
      labelPadding: `${space[0]} ${space[4]}`,
      actionsPadding: `${space[0]} ${space[4]} ${space[0]} ${space[0]}`,
      dismissSectionPadding: `${space[0]} ${space[2]}`,
      dividerHeight: space[4],
    },
  },
  dialog: {
    header: {
      padding: space[2],
    },
  },
  emptyState: {
    padding: {
      top: space[2],
      right: space[3],
      bottom: space[3],
      left: space[3],
    },
    margin: {
      button: {
        top: space[1],
        bottom: space[0],
      },
      link: {
        top: space[1],
        bottom: space[1],
      },
    },
  },
  iconButton: {
    padding: {
      comfortable: space[1],
      compact: space[0],
    },
  },
  input: {
    padding: `calc(${space[1]} - 1px)`, // account for border & line-height
  },
  menu: {
    label: {
      padding: `${space[1]} ${space[2]}`,
    },
    item: {
      nestedPadding: `${space[1]} ${space[1]} ${space[1]} ${space[2]}`,
      padding: `${space[1]} ${space[2]}`,
      prefix: {
        height: '16px',
      },
    },
    content: {
      padding: `${space[1]} 0px`,
    },
    divider: {
      margin: `${space[1]} 0px`,
    },
  },
  modal: {
    header: {
      padding: space[1],
    },
  },
  // navbar is not used in medium (Designer)
  navbar: {
    height: {
      dashboard: '48px',
      marketplace: '48px',
    },
  },
  numberInput: {
    input: {
      padding: space[1],
      height: space[6],
      withPrefixOrSuffix: {
        paddingX: space[2],
        minWidth: space[6],
      },
    },
    controls: {
      width: space[4],
      height: 'auto',
    },
    stepper: {
      input: {
        width: space[6],
      },
      button: {
        width: space[6],
        height: space[6],
      },
    },
  },
  popover: {
    footer: {
      padding: space[2],
      spacing: space[2],
    },
    header: {
      iconButtonHeight: space[6],
      padding: space[1],
      spacing: space[1],
    },
    headerContent: {
      padding: `0px ${space[1]}`,
    },
  },
  rowItem: {
    checkbox: {
      marginTop: `calc(${space[0.5]} + 1px)`,
    },
    prefix: {
      marginTop: space[0.25],
    },
    dragHandle: {
      checkboxHandleWidth: `calc(${space[2]} - 1px)`,
      paddingLeft: space[0.5],
    },
    padding: {
      comfortable: {
        vertical: space[1],
        right: space[1],
        left: space[2],
      },
      compact: {
        vertical: space[0],
        right: space[1],
        left: space[2],
      },
    },
    spacing: space[2],
    content: {
      spacing: space[1],
    },
  },
  segmentedControl: {
    /**
     * This is the SegmentedControlButton height.
     * Derived from the desired height of the SegmentedControl (24px) minus padding (top: 1px, bottom: 1px)
     */
    height: '22px !important',
  },
  select: {
    control: {
      buttonPadding: `${space[1]} ${space[1]}`,
      chevronRightPadding: space[1],
      padding: `${space[1]} ${space[1]}`,
    },
    content: {
      padding: space[1],
    },
    option: {
      padding: `${space[1]} ${space[2]}`,
      spacing: space[1],
    },
    groupHeader: {
      padding: `${space[0]} ${space[2]} ${space[1]} ${space[2]}`,
    },
  },
  switch: {
    track: {
      comfortable: {
        height: space[5],
        width: space[8],
      },
      compact: {
        height: space[4],
        width: space[6],
      },
    },
    knob: {
      comfortable: {
        height: space[4],
        width: space[4],
      },
      compact: {
        height: space[3],
        width: space[3],
      },
    },
  },
  table: {
    emptyState: {
      paddingTop: space[4],
      paddingRight: space[3],
      paddingBottom: space[4],
      paddingLeft: space[3],
    },
    padding: {
      comfortable: `${space[3]} ${space[4]}`,
      compact: `${space[2]} ${space[3]}`,
    },
  },
  tabs: {
    tab: {
      height: '40px',
      margin: space[0.25],
      button: {
        comfortable: {
          padding: {
            horizontal: space[4],
            vertical: space[1],
            column: space[3],
          },
        },
        compact: {
          padding: {
            horizontal: '6px',
            vertical: space[1],
            column: space[3],
          },
        },
      },
      underline: {
        comfortable: {
          padding: {
            horizontal: space[2],
            vertical: space[3],
            column: space[2],
          },
        },
        compact: {
          padding: {
            horizontal: space[0],
            vertical: space[3],
            column: space[2],
          },
        },
      },
    },
    tabList: {
      spacing: {
        underline: {
          row: space[3],
          column: space[3],
        },
        button: {
          row: space[1],
          column: space[3],
        },
      },
    },
    tabPanel: {
      padding: `${space[4]} 0px`,
    },
  },
  timeline: {
    header: {
      padding: space[3],
    },
    body: {
      padding: space[3],
    },
  },
  toast: {
    body: {
      gap: `${space[1]}`,
    },
  },
  tooltip: {
    padding: {
      comfortable: space[2],
      compact: `${space[0.5]} ${space[1]}`,
      commentBubble: `${space[3]} ${space[2]} ${space[4]} ${space[2]}`,
    },
  },
} as const;

const sizingValues = {
  typography,
  ui,
};

// We're grabbing the sizeTokens from here to use for all the themes.
export const {
  tokens: {typography: typographyTokens, ui: uiTokens},
  setTokens: mediumVars,
} = generateTokensFromObject(sizingValues, {
  applyNamespace: true,
});
